<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle :pageTitle="isEditing ? 'Edit Partner' : 'Add Partner'" subTitle="Management" />

    <div class="card bg-white border-0 rounded-3 mb-4">
      <div class="card-body p-4">
        <form @submit.prevent="submitForm">
          <div class="row">
            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Partner Name</label>
              <input type="text" class="form-control" v-model="form.name" placeholder="Enter partner / university name" required />
            </div>

            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">MoU Link (Optional)</label>
              <input type="url" class="form-control" v-model="form.mou_link" placeholder="https://example.com/mou.pdf" />
            </div>

            <div class="col-lg-12 col-md-12 mb-4 d-flex align-items-center">
              <div class="form-check form-switch mt-4">
                <input class="form-check-input" type="checkbox" id="isActive" v-model="form.is_active" />
                <label class="form-check-label text-dark fs-14 fw-medium" for="isActive">Active Status (Visible to users)</label>
              </div>
            </div>

            <div class="col-lg-12 col-md-12 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Partner Logo File</label>
              <div class="d-flex align-items-center gap-3 flex-wrap">
                <!-- Image Preview Box -->
                <div v-if="previewUrl" class="border rounded p-2 bg-light d-flex align-items-center justify-content-center" style="width: 150px; height: 100px;">
                  <img :src="previewUrl" alt="logo preview" style="max-width: 100%; max-height: 100%; object-fit: contain;" />
                </div>
                
                <!-- Upload Input -->
                <div class="flex-grow-1">
                  <input type="file" ref="fileInput" class="form-control" accept="image/*" @change="onFileChange" :required="!isEditing" />
                  <small class="text-muted d-block mt-1">Recommended size: landscape aspect ratio, transparent PNG or clean SVG. Max size: 2MB.</small>
                </div>
              </div>
            </div>
          </div>
          
          <div class="d-flex justify-content-end gap-2">
            <RouterLink to="/partners" class="btn btn-secondary fs-15 fw-medium">Cancel</RouterLink>
            <button type="submit" class="btn btn-primary fs-15 fw-medium" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {{ isEditing ? 'Update Partner' : 'Create Partner' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import PageTitle from '@/components/Common/PageTitle.vue';
import partnerService from '@/services/partnerService';

export default defineComponent({
  name: 'PartnerFormPage',
  components: {
    PageTitle
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    
    const isEditing = ref(false);
    const partnerId = ref<number | null>(null);
    const loading = ref(false);
    const selectedFile = ref<File | null>(null);
    const previewUrl = ref<string>('');

    const form = ref({
      name: '',
      mou_link: '',
      is_active: true
    });

    onMounted(async () => {
      if (route.params.id) {
        isEditing.value = true;
        partnerId.value = Number(route.params.id);
        await fetchPartner(partnerId.value);
      }
    });

    const getFullUrl = (logo: string) => {
      if (!logo) return '';
      if (logo.startsWith('http')) return logo;
      const baseUrl = process.env.NODE_ENV === 'production'
        ? 'https://backend-beta.rayacheck.id'
        : 'http://jurnal-backend.test';
      return `${baseUrl}/storage/${logo}`;
    };

    const fetchPartner = async (id: number) => {
      try {
        const response = await partnerService.getById(id);
        const data = response.data;
        form.value = {
          name: data.name,
          mou_link: data.mou_link || '',
          is_active: !!data.is_active
        };
        if (data.logo) {
          previewUrl.value = getFullUrl(data.logo);
        }
      } catch (error) {
        console.error('Error fetching partner:', error);
        router.push('/partners');
      }
    };

    const onFileChange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files[0]) {
        const file = target.files[0];
        selectedFile.value = file;
        
        // Show local preview
        const reader = new FileReader();
        reader.onload = (event) => {
          previewUrl.value = event.target?.result as string;
        };
        reader.readAsDataURL(file);
      }
    };

    const submitForm = async () => {
      loading.value = true;
      try {
        const formData = new FormData();
        formData.append('name', form.value.name);
        if (form.value.mou_link) {
          formData.append('mou_link', form.value.mou_link);
        }
        formData.append('is_active', form.value.is_active ? '1' : '0');
        if (selectedFile.value) {
          formData.append('logo', selectedFile.value);
        }

        if (isEditing.value && partnerId.value) {
          await partnerService.update(partnerId.value, formData);
        } else {
          await partnerService.create(formData);
        }
        router.push('/partners');
      } catch (error: any) {
        console.error('Error saving partner:', error);
        if (error.response?.data?.message) {
          alert(error.response.data.message);
        } else {
          alert('Failed to save partner.');
        }
      } finally {
        loading.value = false;
      }
    };

    return {
      isEditing,
      form,
      loading,
      previewUrl,
      onFileChange,
      submitForm
    };
  }
});
</script>
